import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DirectionsRun as TrainingIcon,
  Notes as NotesIcon,
  Restaurant as FeedIcon,
  Save as SaveIcon,
  Swipe as SwipeIcon,
  WaterDrop as WaterIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import { useLicense } from '../context/LicenseContext';
import {
  DIARY_FIELDS,
  DIARY_FIELD_LIMITS,
  DIARY_WEEK_START_SETTING,
  WEEK_START_OPTIONS,
  emptyDiaryEntry,
  formatDateKey,
  formatWeekRange,
  getWeekDays,
  normalizeWeekStartDay,
  parseDateKey,
  shiftWeek,
  startOfWeek
} from '../utils/diaryWeek';

const AUTO_SAVE_DELAY = 1200;
const READ_ONLY_UNSAVED_MESSAGE =
  'Your latest diary changes are still on this screen but cannot be saved while the app is read-only. Restore your license before leaving this page.';

const fieldIcons = {
  feed: FeedIcon,
  water: WaterIcon,
  training: TrainingIcon,
  notes: NotesIcon
};

const WeeklyDiary = () => {
  const { state: licenseState } = useLicense();
  const readOnly = licenseState?.mode === 'readonly' || licenseState?.mode === 'unlicensed';
  const [weekStart, setWeekStart] = useState(() => formatDateKey(startOfWeek()));
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [preferenceLoading, setPreferenceLoading] = useState(true);
  const [preferenceSaving, setPreferenceSaving] = useState(false);
  const [navigationPending, setNavigationPending] = useState(false);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [calendarDate, setCalendarDate] = useState(() => formatDateKey(new Date()));
  const days = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const [entries, setEntries] = useState({});
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);

  const requestRef = useRef(0);
  const entriesRef = useRef(entries);
  const weekStartRef = useRef(weekStart);
  const dirtyRef = useRef(false);
  const revisionRef = useRef(0);
  const dirtyCellsRef = useRef(new Map());
  const saveChainRef = useRef(Promise.resolve());

  useEffect(() => { entriesRef.current = entries; }, [entries]);
  useEffect(() => { weekStartRef.current = weekStart; }, [weekStart]);

  useEffect(() => {
    let active = true;
    DatabaseService.getSetting(DIARY_WEEK_START_SETTING)
      .then((storedDay) => {
        if (!active) return;
        const firstDay = normalizeWeekStartDay(storedDay);
        const currentWeek = formatDateKey(startOfWeek(new Date(), firstDay));
        setWeekStartDay(firstDay);
        setWeekStart(currentWeek);
        setCalendarDate(currentWeek);
      })
      .catch((preferenceError) => {
        console.error('Failed to load diary week-start preference:', preferenceError);
      })
      .finally(() => {
        if (active) setPreferenceLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const warnBeforeUnload = (event) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, []);

  useEffect(() => {
    if (readOnly && dirtyRef.current) {
      setSaveState('error');
      setError(READ_ONLY_UNSAVED_MESSAGE);
    }
  }, [readOnly]);

  useEffect(() => {
    if (preferenceLoading) return undefined;
    const requestId = ++requestRef.current;
    const firstDay = days[0].key;
    const lastDay = days[6].key;
    const blankWeek = Object.fromEntries(days.map((day) => [day.key, emptyDiaryEntry(day.key)]));
    setLoading(true);
    setError('');
    setLoadFailed(false);
    entriesRef.current = blankWeek;
    dirtyRef.current = false;
    dirtyCellsRef.current.clear();
    setEntries(blankWeek);
    setDirty(false);

    DatabaseService.getDiaryEntries(firstDay, lastDay)
      .then((rows) => {
        if (requestId !== requestRef.current) return;
        const next = Object.fromEntries(days.map((day) => [day.key, emptyDiaryEntry(day.key)]));
        for (const row of rows) {
          if (next[row.entry_date]) next[row.entry_date] = { ...next[row.entry_date], ...row };
        }
        entriesRef.current = next;
        dirtyRef.current = false;
        dirtyCellsRef.current.clear();
        setEntries(next);
        setDirty(false);
        setSaveState('idle');
      })
      .catch((loadError) => {
        if (requestId !== requestRef.current) return;
        console.error('Failed to load weekly diary:', loadError);
        setLoadFailed(true);
        setError('Could not load this diary week. Please try again.');
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [days, loadNonce, preferenceLoading]);

  const persistEntries = useCallback(() => {
    if (!dirtyRef.current) return Promise.resolve(true);
    if (readOnly) {
      setSaveState('error');
      setError(READ_ONLY_UNSAVED_MESSAGE);
      return Promise.resolve(false);
    }

    const targetWeek = weekStartRef.current;
    const targetRevisions = new Map(
      [...dirtyCellsRef.current].map(([entryDate, fields]) => [entryDate, new Map(fields)])
    );
    const snapshot = getWeekDays(targetWeek)
      .filter((day) => targetRevisions.has(day.key))
      .map((day) => {
        const patch = { entry_date: day.key };
        for (const field of targetRevisions.get(day.key).keys()) {
          patch[field] = entriesRef.current[day.key]?.[field] || '';
        }
        return patch;
      });
    if (snapshot.length === 0) return Promise.resolve(true);

    const save = async () => {
      setSaveState('saving');
      setError('');
      try {
        await DatabaseService.saveDiaryWeek(targetWeek, snapshot);
        if (weekStartRef.current === targetWeek) {
          for (const [entryDate, fields] of targetRevisions) {
            const currentFields = dirtyCellsRef.current.get(entryDate);
            if (!currentFields) continue;
            for (const [field, revision] of fields) {
              if (currentFields.get(field) === revision) currentFields.delete(field);
            }
            if (currentFields.size === 0) dirtyCellsRef.current.delete(entryDate);
          }
          const stillDirty = dirtyCellsRef.current.size > 0;
          dirtyRef.current = stillDirty;
          setDirty(stillDirty);
          setSaveState(stillDirty ? 'unsaved' : 'saved');
        }
        return true;
      } catch (saveError) {
        console.error('Failed to save weekly diary:', saveError);
        if (weekStartRef.current === targetWeek) {
          setSaveState('error');
          setError(saveError?.message || 'Could not save this diary week. Please try again.');
        }
        return false;
      }
    };

    const queued = saveChainRef.current.then(save, save);
    saveChainRef.current = queued.then(() => undefined, () => undefined);
    return queued;
  }, [readOnly]);

  useEffect(() => {
    if (!dirty || readOnly) return undefined;
    const timer = window.setTimeout(() => { persistEntries(); }, AUTO_SAVE_DELAY);
    return () => window.clearTimeout(timer);
  }, [dirty, entries, persistEntries, readOnly]);

  const updateCell = (entryDate, field, value) => {
    if (readOnly || loading || loadFailed || navigationPending || preferenceSaving) return;
    revisionRef.current += 1;
    if (!dirtyCellsRef.current.has(entryDate)) dirtyCellsRef.current.set(entryDate, new Map());
    dirtyCellsRef.current.get(entryDate).set(field, revisionRef.current);
    dirtyRef.current = true;
    setDirty(true);
    setSaveState('unsaved');
    const next = {
      ...entriesRef.current,
      [entryDate]: {
        ...(entriesRef.current[entryDate] || emptyDiaryEntry(entryDate)),
        [field]: value
      }
    };
    entriesRef.current = next;
    setEntries(next);
  };

  const moveWeek = async (amount) => {
    setNavigationPending(true);
    try {
      const saved = await persistEntries();
      if (!saved) return;
      setLoading(true);
      setWeekStart((current) => shiftWeek(current, amount));
    } finally {
      setNavigationPending(false);
    }
  };

  const goToCurrentWeek = async () => {
    const currentWeek = formatDateKey(startOfWeek(new Date(), weekStartDay));
    if (currentWeek === weekStart) return;
    setNavigationPending(true);
    try {
      const saved = await persistEntries();
      if (!saved) return;
      setLoading(true);
      setWeekStart(currentWeek);
    } finally {
      setNavigationPending(false);
    }
  };

  const openCalendar = (event) => {
    setCalendarDate(weekStart);
    setCalendarAnchorEl(event.currentTarget);
  };

  const closeCalendar = () => setCalendarAnchorEl(null);

  const jumpToSelectedWeek = async (event) => {
    event.preventDefault();
    const selectedDate = parseDateKey(calendarDate);
    if (!selectedDate) {
      setError('Choose a valid calendar date.');
      return;
    }
    const selectedWeek = formatDateKey(startOfWeek(selectedDate, weekStartDay));
    if (selectedWeek === weekStartRef.current) {
      setError('');
      closeCalendar();
      return;
    }
    setNavigationPending(true);
    try {
      const saved = await persistEntries();
      if (!saved) return;
      setError('');
      setLoading(true);
      setWeekStart(selectedWeek);
      closeCalendar();
    } finally {
      setNavigationPending(false);
    }
  };

  const changeWeekStartDay = async (event) => {
    const nextDay = normalizeWeekStartDay(event.target.value, weekStartDay);
    if (nextDay === weekStartDay) return;
    setNavigationPending(true);
    try {
      const saved = await persistEntries();
      if (!saved) return;
      setPreferenceSaving(true);
      setError('');
      await DatabaseService.setSetting(DIARY_WEEK_START_SETTING, String(nextDay));
      const anchorDate = parseDateKey(weekStartRef.current) || new Date();
      const nextWeek = formatDateKey(startOfWeek(anchorDate, nextDay));
      setWeekStartDay(nextDay);
      setLoading(true);
      setWeekStart(nextWeek);
      setCalendarDate(nextWeek);
    } catch (preferenceError) {
      console.error('Failed to save diary week-start preference:', preferenceError);
      setError(preferenceError?.message || 'Could not save the first day of the week. Please try again.');
    } finally {
      setPreferenceSaving(false);
      setNavigationPending(false);
    }
  };

  const todayKey = formatDateKey(new Date());
  const currentWeekKey = formatDateKey(startOfWeek(new Date(), weekStartDay));
  const navigationBusy = loading || preferenceLoading || preferenceSaving || navigationPending || saveState === 'saving';
  const calendarOpen = Boolean(calendarAnchorEl);

  const saveLabel = (() => {
    if (readOnly) return 'Read only';
    if (saveState === 'saving') return 'Saving…';
    if (saveState === 'saved') return 'Saved';
    if (saveState === 'error') return 'Retry save';
    return dirty ? 'Save changes' : 'Saved';
  })();

  return (
    <Box sx={{ maxWidth: 1800, mx: 'auto', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' }
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontSize: { xs: '1.65rem', sm: '2.125rem' }, lineHeight: 1.18 }}
          >
            Weekly Loft Diary
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, maxWidth: 700, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
            Record the feed, water treatments, training, and observations for every day of the week.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saveState === 'saving' ? <CircularProgress size={17} color="inherit" /> : <SaveIcon />}
          disabled={readOnly || loading || loadFailed || navigationPending || preferenceSaving || saveState === 'saving' || (!dirty && saveState !== 'error')}
          onClick={persistEntries}
          sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
        >
          {saveLabel}
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          action={!loading ? (
            loadFailed
              ? <Button color="inherit" onClick={() => setLoadNonce((value) => value + 1)}>Retry</Button>
              : (dirty && !readOnly ? <Button color="inherit" onClick={persistEntries}>Retry</Button> : null)
          ) : null}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your diary is available to read, but an active trial or license is required to add or change entries.
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{ mb: 2, p: { xs: 1, sm: 1.5 } }}
      >
        <Box
          sx={{
            display: 'grid', alignItems: 'center', gap: { xs: 0.5, sm: 1 },
            gridTemplateColumns: '44px minmax(0, 1fr) 44px'
          }}
        >
          <Tooltip title="Previous week">
            <span>
              <IconButton
                aria-label="Show previous diary week"
                onClick={() => moveWeek(-1)}
                disabled={navigationBusy}
                sx={{ width: 44, height: 44 }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ textAlign: 'center', minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">Week</Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.25, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {formatWeekRange(weekStart)}
            </Typography>
          </Box>

          <Tooltip title="Next week">
            <span>
              <IconButton
                aria-label="Show next diary week"
                onClick={() => moveWeek(1)}
                disabled={navigationBusy}
                sx={{ width: 44, height: 44 }}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center',
            gap: 1, mt: 1.25, pt: 1.25, borderTop: '1px solid', borderTopColor: 'divider'
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon />}
            onClick={openCalendar}
            disabled={navigationBusy}
            aria-haspopup="dialog"
            aria-expanded={calendarOpen}
            aria-controls={calendarOpen ? 'diary-week-picker' : undefined}
            sx={{
              minHeight: 44, order: 1,
              flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' }
            }}
          >
            Choose week
          </Button>

          <Box
            aria-busy={preferenceSaving}
            sx={{
              position: 'relative', order: { xs: 3, sm: 2 },
              flex: { xs: '1 1 100%', sm: '0 0 180px' }
            }}
          >
            <FormControl fullWidth size="small" disabled={navigationBusy || readOnly}>
              <InputLabel id="diary-week-start-label">Week starts</InputLabel>
              <Select
                labelId="diary-week-start-label"
                id="diary-week-start"
                value={weekStartDay}
                label="Week starts"
                onChange={changeWeekStartDay}
                sx={{ minHeight: 44 }}
              >
                {WEEK_START_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {preferenceSaving && (
              <CircularProgress
                size={17}
                aria-label="Saving first day of the week"
                sx={{ position: 'absolute', right: 36, top: '50%', mt: '-8.5px' }}
              />
            )}
          </Box>

          <Button
            size="small"
            onClick={goToCurrentWeek}
            disabled={weekStart === currentWeekKey || navigationBusy}
            sx={{
              minHeight: 44, order: { xs: 2, sm: 3 },
              flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' }
            }}
          >
            Today
          </Button>
        </Box>
      </Paper>

      <Popover
        id="diary-week-picker"
        open={calendarOpen}
        anchorEl={calendarAnchorEl}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          role: 'dialog',
          'aria-labelledby': 'diary-week-picker-title',
          'aria-describedby': 'diary-week-picker-help',
          sx: { width: 310, maxWidth: 'calc(100vw - 32px)', borderRadius: 2 }
        }}
      >
        <Box component="form" onSubmit={jumpToSelectedWeek} sx={{ p: 2 }}>
          <Typography id="diary-week-picker-title" variant="subtitle1" sx={{ fontWeight: 700 }}>
            Choose a diary week
          </Typography>
          <Typography id="diary-week-picker-help" variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
            Pick any date and the diary will open the week containing it.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="date"
            label="Date"
            value={calendarDate}
            onChange={(event) => setCalendarDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-describedby': 'diary-week-picker-help' }}
            sx={{ '& .MuiInputBase-root': { minHeight: 44 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={closeCalendar} sx={{ minHeight: 44 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!calendarDate || navigationBusy} sx={{ minHeight: 44 }}>
              Go to week
            </Button>
          </Box>
        </Box>
      </Popover>

      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1,
          color: 'text.secondary', mb: 1.25
        }}
      >
        <SwipeIcon fontSize="small" />
        <Typography variant="caption">Swipe sideways to view each day of the week.</Typography>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        aria-busy={loading}
        sx={{
          position: 'relative', overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain', borderRadius: 2
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute', inset: 0, zIndex: 5, display: 'flex', alignItems: 'center',
              justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.78)'
            }}
          >
            <CircularProgress size={30} aria-label="Loading diary week" />
          </Box>
        )}
        <Table
          aria-label={`Weekly loft diary for ${formatWeekRange(weekStart)}`}
          sx={{ minWidth: { xs: 1032, sm: 1260 }, tableLayout: 'fixed' }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                scope="col"
                sx={{
                  position: 'sticky', left: 0, zIndex: 3,
                  width: { xs: 96, sm: 120 }, minWidth: { xs: 96, sm: 120 },
                  bgcolor: 'grey.50', borderRight: '1px solid', borderRightColor: 'divider'
                }}
              >
                Routine
              </TableCell>
              {days.map((day) => {
                const isToday = day.key === todayKey;
                return (
                  <TableCell
                    key={day.key}
                    scope="col"
                    align="center"
                    sx={{
                      width: { xs: 132, sm: 160 },
                      minWidth: { xs: 132, sm: 160 },
                      py: { xs: 1, sm: 1.5 },
                      px: { xs: 0.75, sm: 2 },
                      bgcolor: isToday ? 'primary.lighter' : 'grey.50'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" sx={{ textTransform: 'none' }}>{day.dayName}</Typography>
                      {isToday && <Chip label="Today" size="small" color="primary" />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{day.shortDate}</Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {DIARY_FIELDS.map((field) => {
              const FieldIcon = fieldIcons[field.key];
              return (
                <TableRow key={field.key}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      position: 'sticky', left: 0, zIndex: 2,
                      width: { xs: 96, sm: 120 }, minWidth: { xs: 96, sm: 120 },
                      bgcolor: 'background.paper', borderRight: '1px solid', borderRightColor: 'divider',
                      verticalAlign: 'top', pt: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, color: 'text.primary' }}>
                      <FieldIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>{field.label}</Typography>
                    </Box>
                  </TableCell>
                  {days.map((day) => (
                    <TableCell
                      key={day.key}
                      sx={{
                        p: { xs: 0.85, sm: 1.25 }, verticalAlign: 'top',
                        bgcolor: day.key === todayKey ? 'rgba(44,90,160,0.035)' : 'background.paper'
                      }}
                    >
                      <TextField
                        value={entries[day.key]?.[field.key] || ''}
                        onChange={(event) => updateCell(day.key, field.key, event.target.value)}
                        onBlur={() => { if (dirtyRef.current) persistEntries(); }}
                        placeholder={field.placeholder}
                        multiline
                        minRows={3}
                        maxRows={7}
                        fullWidth
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          readOnly: readOnly || loading || loadFailed || navigationPending || preferenceSaving
                        }}
                        inputProps={{
                          maxLength: DIARY_FIELD_LIMITS[field.key],
                          'aria-label': `${field.label} for ${day.dayName}, ${day.shortDate}`
                        }}
                        sx={{
                          '& textarea': {
                            fontSize: { xs: '1rem', sm: '0.925rem' },
                            lineHeight: 1.5,
                            resize: 'vertical',
                            minHeight: { xs: 72, sm: 'auto' }
                          },
                          '& textarea::placeholder': { color: 'text.disabled', opacity: 1 }
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, minHeight: 24 }} aria-live="polite">
        {!readOnly && saveState === 'saving' && <Typography variant="caption" color="text.secondary">Saving your diary…</Typography>}
        {!readOnly && saveState === 'saved' && <Typography variant="caption" color="success.main">All changes saved.</Typography>}
        {!readOnly && saveState === 'unsaved' && <Typography variant="caption" color="text.secondary">Changes will save automatically.</Typography>}
      </Box>
    </Box>
  );
};

export default WeeklyDiary;
